import React, { useEffect, useState } from "react";
import Parse from "parse";
import ModalUi from "../primitives/ModalUi";
import DeleteTeamConfirmation from "./DeleteTeamConfirmation";

const EditTeam = ({ isOpen, onClose, team, onSuccess }) => {
  const [teamName, setTeamName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [hasDocuments, setHasDocuments] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    if (isOpen && team) {
      loadTeamData();
    }
  }, [isOpen, team]);

  const loadTeamData = async () => {
    try {
      setIsLoading(true);
      setTeamName(team.Name || "");

      // Load team members
      await loadTeamMembers();

      // Check if team has documents
      const docCheck = await checkTeamHasDocuments();
      setHasDocuments(docCheck);

      // Load all users for potential document transfer
      await loadAllUsers();
    } catch (err) {
      console.error("Error loading team data:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const result = await Parse.Cloud.run("getteammembers", {
        teamId: team.objectId,
        excludeOrgAdmins: true // Exclude org admins from count for deletion purposes
      });
      if (result) {
        const members = JSON.parse(JSON.stringify(result));
        setTeamMembers(members);
      }
    } catch (err) {
      console.error("Error loading team members:", err);
    }
  };

  const checkTeamHasDocuments = async () => {
    try {
      const docQuery = new Parse.Query("contracts_Document");
      docQuery.equalTo("TeamId", {
        __type: "Pointer",
        className: "contracts_Teams",
        objectId: team.objectId
      });
      docQuery.limit(1);
      const count = await docQuery.count();
      return count > 0;
    } catch (err) {
      console.error("Error checking team documents:", err);
      return false;
    }
  };

  const loadAllUsers = async () => {
    try {
      const result = await Parse.Cloud.run("getallusers", {});
      if (result) {
        const users = JSON.parse(JSON.stringify(result));
        setAllUsers(users);
      }
    } catch (err) {
      console.error("Error loading all users:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!teamName.trim()) {
      setError("Team name is required");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const data = { Name: teamName, TeamId: team.objectId };
      await Parse.Cloud.run("updateteam", data);

      if (onSuccess) {
        onSuccess("Team updated successfully");
      }
      handleClose();
    } catch (err) {
      console.error("Error updating team:", err);
      setError(err.message || "Failed to update team");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTeam = async (deleteOptions) => {
    try {
      const result = await Parse.Cloud.run("deleteorcloseteam", {
        teamId: team.objectId,
        action: deleteOptions.action,
        transferDocuments: deleteOptions.transferDocuments,
        targetUserId: deleteOptions.targetUserId
      });

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.message);
        }
        handleClose();
      }
    } catch (err) {
      console.error("Error deleting/closing team:", err);
      throw err;
    }
  };

  const handleClose = () => {
    setTeamName("");
    setError("");
    setShowDeleteModal(false);
    setTeamMembers([]);
    setHasDocuments(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalUi
        isOpen={isOpen}
        title="Edit Team"
        handleClose={handleClose}
        showClose={!isLoading}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              Team Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => {
                setTeamName(e.target.value);
                setError("");
              }}
              className="op-input op-input-bordered w-full"
              disabled={isLoading}
            />
          </div>

          {teamMembers.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Members:</strong> {teamMembers.length} user(s) in this
                team
              </p>
            </div>
          )}

          {hasDocuments && (
            <div className="bg-orange-50 border border-orange-200 rounded p-3">
              <p className="text-sm text-orange-800">
                <strong>Note:</strong> This team has documents assigned
              </p>
            </div>
          )}

          <div className="flex justify-between gap-2 pt-4">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="op-btn op-btn-error"
              disabled={isLoading}
            >
              Delete/Close Team
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="op-btn op-btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="op-btn op-btn-primary"
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </ModalUi>

      {/* Delete/Close Team Modal */}
      {showDeleteModal && (
        <DeleteTeamConfirmation
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          team={team}
          teamMembers={teamMembers}
          hasDocuments={hasDocuments}
          userList={allUsers}
          onConfirm={handleDeleteTeam}
        />
      )}
    </>
  );
};

export default EditTeam;
